import React, { useState } from 'react';
import { DollarSign, Calendar, FileText, StickyNote } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FileUploadField from '../ui/FileUploadField';
import { formatDateForAPI } from '../../utils/dateUtils';
import { useCreateInvoice } from '../../hooks/useInvoices';
import { useAllServices } from '../../hooks/useServices';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const AddInvoiceModal = ({ isOpen, onClose, service = null }) => {
    const initServiceKey = service ? `${service.supplierUid}::${service.uid}` : '';

    const [formData, setFormData] = useState({
        serviceKey: initServiceKey,
        name: '',
        amount: '',
        issueDate: '',
        dueDate: '',
        paidDate: '',
        isPaid: false,
        notes: '',
        fileUid: null,
        paymentProofFileUid: null,
    });

    const [isUploading, setIsUploading] = useState(false);

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const createInvoice = useCreateInvoice();
    const { data: allServices = [], isLoading: loadingServices } = useAllServices();
    const { t } = useLanguage();

    const validateForm = () => {
        const newErrors = {};

        if (!service && !formData.serviceKey) {
            newErrors.serviceKey = t(KEYS.INVOICE_FIELD_SERVICE_REQUIRED);
        }

        if (formData.name && formData.name.length > 191) {
            newErrors.name = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        if (formData.amount && parseFloat(formData.amount) < 0) {
            newErrors.amount = t(KEYS.INVOICE_AMOUNT_ERROR);
        }

        if (formData.dueDate && formData.issueDate && formData.dueDate < formData.issueDate) {
            newErrors.dueDate = t(KEYS.INVOICE_DUE_DATE_ERROR);
        }

        if (formData.paidDate && formData.issueDate && formData.paidDate < formData.issueDate) {
            newErrors.paidDate = t(KEYS.INVOICE_PAID_DATE_ERROR);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const activeServiceKey = service ? `${service.supplierUid}::${service.uid}` : formData.serviceKey;
            const [supplierUid, serviceUid] = activeServiceKey.split('::');

            const data = {};
            if (formData.name) data.name = formData.name;
            if (formData.amount) data.amount = parseFloat(formData.amount);
            if (formData.issueDate) data.issue_date = formatDateForAPI(formData.issueDate);
            if (formData.dueDate) data.due_date = formatDateForAPI(formData.dueDate);
            if (formData.paidDate) data.paid_date = formatDateForAPI(formData.paidDate);
            data.is_paid = formData.isPaid;
            if (formData.notes) data.notes = formData.notes;
            if (formData.fileUid) data.file_uid = formData.fileUid;
            if (formData.paymentProofFileUid) data.payment_proof_file_uid = formData.paymentProofFileUid;

            await createInvoice.mutateAsync({ supplierUid, serviceUid, data });

            setFormData({
                serviceKey: '',
                name: '',
                amount: '',
                issueDate: '',
                dueDate: '',
                paidDate: '',
                isPaid: false,
                notes: '',
                fileUid: null,
                paymentProofFileUid: null,
            });
            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.INVOICE_ADD_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData({
                serviceKey: initServiceKey,
                name: '',
                amount: '',
                issueDate: '',
                dueDate: '',
                paidDate: '',
                isPaid: false,
                notes: '',
                fileUid: null,
                paymentProofFileUid: null,
            });
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.INVOICE_ADD_TITLE)}
            subtitle="Create a new invoice"
            size="medium"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleSubmit} disabled={isSubmitting || (!service && loadingServices) || isUploading}>
                        {isSubmitting ? t(KEYS.INVOICE_ADD_SUBMITTING) : t(KEYS.INVOICE_ADD_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm mb-4">
                    {submitError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Service — read-only when pre-selected, dropdown otherwise */}
                {service ? (
                    <div className="space-y-1">
                        <label className="label-base flex items-center gap-1.5">
                            <FileText className="w-4 h-4" />
                            Service
                        </label>
                        <div className="input-base bg-surface-overlay text-text-muted cursor-not-allowed">
                            {service.name || service.type}{service.supplierName ? ` — ${service.supplierName}` : ''}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-1">
                        <label htmlFor="serviceKey" className="label-base flex items-center gap-1.5">
                            <FileText className="w-4 h-4" />
                            Service <span className="text-red-500">*</span>
                        </label>
                        {loadingServices ? (
                            <div className="text-sm text-text-muted text-center py-2">
                                {t(KEYS.INVOICE_FIELD_LOADING_SERVICES)}
                            </div>
                        ) : (
                            <select
                                id="serviceKey"
                                name="serviceKey"
                                value={formData.serviceKey}
                                onChange={handleChange}
                                className={`input-base ${errors.serviceKey ? 'border-red-500 focus:ring-red-500' : ''}`}
                                disabled={isSubmitting}
                            >
                                <option value="">{t(KEYS.INVOICE_FIELD_SELECT_SERVICE)}</option>
                                {allServices.map((svc) => (
                                    <option
                                        key={svc.uid}
                                        value={`${svc.supplierUid}::${svc.uid}`}
                                    >
                                        {svc.name || svc.type} — {svc.supplierName}
                                    </option>
                                ))}
                            </select>
                        )}
                        {errors.serviceKey && (
                            <span className="text-xs text-red-500 mt-1">{errors.serviceKey}</span>
                        )}
                    </div>
                )}

                {/* Name */}
                <div className="space-y-1">
                    <label htmlFor="name" className="label-base flex items-center gap-1.5">
                        <FileText className="w-4 h-4" />
                        Name
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`input-base ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., March 2026"
                        maxLength={191}
                        disabled={isSubmitting}
                    />
                    {errors.name && (
                        <span className="text-xs text-red-500 mt-1">{errors.name}</span>
                    )}
                </div>

                {/* Amount */}
                <div className="space-y-1">
                    <label htmlFor="amount" className="label-base flex items-center gap-1.5">
                        <DollarSign className="w-4 h-4" />
                        Amount
                    </label>
                    <input
                        type="number"
                        id="amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        className={`input-base ${errors.amount ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        disabled={isSubmitting}
                    />
                    {errors.amount && (
                        <span className="text-xs text-red-500 mt-1">{errors.amount}</span>
                    )}
                </div>

                {/* Issue Date */}
                <div className="space-y-1">
                    <label htmlFor="issueDate" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Issue Date
                    </label>
                    <input
                        type="date"
                        id="issueDate"
                        name="issueDate"
                        value={formData.issueDate}
                        onChange={handleChange}
                        className={`input-base ${errors.issueDate ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.issueDate && (
                        <span className="text-xs text-red-500 mt-1">{errors.issueDate}</span>
                    )}
                </div>

                {/* Due Date */}
                <div className="space-y-1">
                    <label htmlFor="dueDate" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Due Date
                    </label>
                    <input
                        type="date"
                        id="dueDate"
                        name="dueDate"
                        value={formData.dueDate}
                        onChange={handleChange}
                        className={`input-base ${errors.dueDate ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.dueDate && (
                        <span className="text-xs text-red-500 mt-1">{errors.dueDate}</span>
                    )}
                </div>

                {/* Paid Date */}
                <div className="space-y-1">
                    <label htmlFor="paidDate" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Paid Date
                    </label>
                    <input
                        type="date"
                        id="paidDate"
                        name="paidDate"
                        value={formData.paidDate}
                        onChange={handleChange}
                        className={`input-base ${errors.paidDate ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.paidDate && (
                        <span className="text-xs text-red-500 mt-1">{errors.paidDate}</span>
                    )}
                </div>

                {/* Is Paid Checkbox */}
                <div className="space-y-1">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            name="isPaid"
                            checked={formData.isPaid}
                            onChange={handleChange}
                            className="w-4 h-4 rounded border-surface-border bg-surface-overlay cursor-pointer transition-colors"
                            disabled={isSubmitting}
                        />
                        <span className="text-sm text-text-primary">{t(KEYS.COMMON_MARK_AS_PAID)}</span>
                    </label>
                </div>

                {/* Notes */}
                <div className="space-y-1">
                    <label htmlFor="notes" className="label-base flex items-center gap-1.5">
                        <StickyNote className="w-4 h-4" />
                        Notes
                    </label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="input-base resize-y"
                        placeholder="Additional notes (optional)"
                        rows={3}
                        disabled={isSubmitting}
                    />
                </div>

                {formData.serviceKey && (
                    <>
                        {/* Invoice File */}
                        <FileUploadField
                            label="Invoice File"
                            serviceUid={formData.serviceKey.split('::')[1]}
                            value={formData.fileUid}
                            onChange={(uid) => setFormData((prev) => ({ ...prev, fileUid: uid }))}
                            onUploadingChange={(uploading) => setIsUploading(uploading)}
                            disabled={isSubmitting}
                        />

                        {/* Payment Proof */}
                        <FileUploadField
                            label="Payment Proof"
                            serviceUid={formData.serviceKey.split('::')[1]}
                            value={formData.paymentProofFileUid}
                            onChange={(uid) => setFormData((prev) => ({ ...prev, paymentProofFileUid: uid }))}
                            onUploadingChange={(uploading) => setIsUploading(uploading)}
                            disabled={isSubmitting}
                        />
                    </>
                )}
            </form>
        </Modal>
    );
};

export default AddInvoiceModal;
